import type {GridPoint} from "@/dungeons/model/DungeonGeometry.ts";

const cross = (a: GridPoint, b: GridPoint, c: GridPoint) =>
    (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);

const onSegment = (a: GridPoint, b: GridPoint, c: GridPoint) =>
    b.x >= Math.min(a.x, c.x) && b.x <= Math.max(a.x, c.x) &&
    b.y >= Math.min(a.y, c.y) && b.y <= Math.max(a.y, c.y);

const intersects = (a: GridPoint, b: GridPoint, c: GridPoint, d: GridPoint) => {
    const abC = cross(a, b, c), abD = cross(a, b, d);
    const cdA = cross(c, d, a), cdB = cross(c, d, b);
    if (abC === 0 && onSegment(a, c, b) || abD === 0 && onSegment(a, d, b) ||
        cdA === 0 && onSegment(c, a, d) || cdB === 0 && onSegment(c, b, d)) return true;
    return (abC > 0) !== (abD > 0) && (cdA > 0) !== (cdB > 0);
};

/** Stored polygons omit the repeated closing point. Konva closes the final edge. */
export function validTransitionPolygon(points: readonly GridPoint[]): boolean {
    if (points.length < 3 || points.some(point => !Number.isFinite(point.x) || !Number.isFinite(point.y))) {
        return false;
    }
    if (new Set(points.map(point => `${point.x}:${point.y}`)).size !== points.length) return false;
    const area = points.reduce((sum, point, index) => {
        const next = points[(index + 1) % points.length];
        return sum + point.x * next.y - next.x * point.y;
    }, 0);
    if (Math.abs(area) <= 1e-8) return false;
    for (let first = 0; first < points.length; first++) {
        for (let second = first + 2; second < points.length; second++) {
            if (first === 0 && second === points.length - 1) continue;
            if (intersects(points[first], points[(first + 1) % points.length],
                points[second], points[(second + 1) % points.length])) return false;
        }
    }
    return true;
}

/** Area-weighted center of a valid transition polygon in source grid coordinates. */
export function transitionAreaCenter(points: readonly GridPoint[]): GridPoint {
    let doubleArea = 0;
    let x = 0;
    let y = 0;
    points.forEach((point, index) => {
        const next = points[(index + 1) % points.length];
        const area = point.x * next.y - next.x * point.y;
        doubleArea += area;
        x += (point.x + next.x) * area;
        y += (point.y + next.y) * area;
    });
    return {x: x / (3 * doubleArea), y: y / (3 * doubleArea)};
}
